import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LOGIN } from "../constants/testIds";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { apiErr } from "../api/client";

export default function Login() {
    const { user, loading, loginPin, loginPassword } = useAuth();
    const nav = useNavigate();
    const [mode, setMode] = useState("pin"); // "pin" | "password"
    const [email, setEmail] = useState("");
    const [pin, setPin] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (loading) return null;
    if (user) {
        const dest = user.role === "staff" ? "/m/today" : "/admin";
        return <Navigate to={dest} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            let u;
            if (mode === "pin") u = await loginPin(email.trim().toLowerCase(), pin);
            else u = await loginPassword(email.trim().toLowerCase(), password);
            const dest = u?.role === "staff" ? "/m/today" : "/admin";
            nav(dest, { replace: true });
        } catch (e) {
            setErr(apiErr(e, "Invalid credentials"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={LOGIN.root} className="min-h-screen grid lg:grid-cols-[1.15fr_1fr]">
            {/* left panel: brand */}
            <div className="relative overflow-hidden km-rail hidden lg:flex flex-col justify-between px-14 py-16">
                <div className="absolute inset-0 km-grid opacity-70" />
                <div className="relative">
                    <div className="inline-flex bg-white rounded-2xl px-7 py-6 shadow-[0_20px_40px_-16px_rgba(0,0,0,0.4)]">
                        <img src="/logo.png" alt="KMSurveys" className="h-14 w-auto" draggable={false} />
                    </div>
                </div>
                <div className="relative max-w-lg">
                    <div className="text-[color:#a9b2c1] text-xs uppercase tracking-[0.2em] mb-4">
                        Phase 1 · Locked build
                    </div>
                    <h1 className="text-white text-5xl leading-[1.05] mb-6 font-extrabold tracking-[-0.03em]">
                        Surveying operations, <span className="serif-em text-[color:#F2B71E]">without</span> the paperwork.
                    </h1>
                    <p className="text-[color:#cfd6e4] text-base leading-relaxed">
                        Jobs, clients, rates, and timesheets — one clean shell for the whole crew.
                        Field crews clock in on a PIN. Office runs the ledger.
                    </p>
                </div>
                <div className="relative text-xs text-[color:#8a94a6]">
                    <span className="num">v1.0.0</span> · Brisbane time
                </div>
            </div>

            {/* right panel: form */}
            <div className="flex items-center justify-center p-6 sm:p-12 bg-[color:var(--km-surface-1)]">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden mb-8">
                        <img src="/logo.png" alt="KMSurveys" className="h-10 w-auto" draggable={false} />
                    </div>
                    <h2 className="text-3xl mb-2 font-extrabold tracking-[-0.03em]">Sign in</h2>
                    <p className="text-sm text-muted mb-8">
                        {mode === "pin"
                            ? "Enter your email and 4-digit PIN."
                            : "Enter your email and password."}
                    </p>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label>Email</Label>
                            <Input
                                data-testid={LOGIN.email}
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@kmsurveys.com.au"
                            />
                        </div>

                        {mode === "pin" ? (
                            <div>
                                <Label>PIN</Label>
                                <Input
                                    data-testid={LOGIN.pin}
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                    required
                                    className="num text-center text-2xl tracking-[0.5em]"
                                />
                            </div>
                        ) : (
                            <div>
                                <Label>Password</Label>
                                <Input
                                    data-testid={LOGIN.password}
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        )}

                        {err && (
                            <div data-testid={LOGIN.error} className="text-sm text-[color:var(--km-red)]">
                                {err}
                            </div>
                        )}

                        <Button
                            data-testid={LOGIN.submit}
                            type="submit"
                            className="w-full h-11"
                            disabled={busy}
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>

                        <div className="text-sm text-center pt-2">
                            {mode === "pin" ? (
                                <button
                                    type="button"
                                    data-testid={LOGIN.togglePassword}
                                    onClick={() => { setMode("password"); setErr(""); }}
                                    className="text-accent hover:underline"
                                >
                                    Sign in with password instead
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    data-testid={LOGIN.togglePin}
                                    onClick={() => { setMode("pin"); setErr(""); }}
                                    className="text-accent hover:underline"
                                >
                                    Use PIN instead
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
